(ns io.daskul.webapp.widgets.dashboard.section
  "Dashboard section — titled group of metrics.

   Supports sub-sections: when metrics have a :sub-section key, they are
   grouped and rendered with sub-headings within the section.

   Shows 'all caught up' only when ALL metrics across all sub-sections
   have zero actionable values and no table items."
  (:require
   [uix.core :refer [defui $ use-memo]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.dashboard.metric-grid :as metric-grid]))

(def ^:private all-clear-messages
  ;; Education / teacher themed
  ["Zero inbox, zero stress. Go teach something amazing."
   "The classroom awaits and the paperwork doesn't. Perfect day."
   "All caught up. Your teachers would give you an A+."
   "No pending items. Time to plan that field trip."
   "Nothing to do here. Go inspire a student."
   "Admin done. Back to what matters — the students."
   "Gold star for you. Everything's handled."
   "No homework for you today. Everything's submitted."
   "Class dismissed — you're free to go."
   "Report card: A+ in keeping things organized."
   ;; Admin humor
   "Nothing pending. The paperwork gods are pleased."
   "No follow-ups, no overdue invoices. Is this real life?"
   "All tasks complete. Even your spreadsheet is impressed."
   "If admin work were a game, you just beat it."
   ;; General witty
   "Plot twist: nothing's on fire today."
   "The to-do list is empty. We checked twice."
   "Everything's handled. You've earned that coffee."
   "All clear. Your future self thanks you."
   "Somebody pinch us. There's literally nothing pending."
   "This dashboard is so clean you could eat off it."])

(defn- has-sub-sections? [metrics]
  (some :sub-section metrics))

(defn- all-clear?
  "True when every actionable stat is zero and no table has items."
  [metrics]
  (let [actionable (filter :actionable metrics)
        has-items? (some #(seq (:items %)) metrics)]
    (and (seq actionable)
         (every? #(zero? (:value %)) actionable)
         (not has-items?))))

(defn- group-by-sub-section
  "Group metrics by :sub-section, preserving order of first appearance."
  [metrics]
  (let [order (distinct (keep :sub-section metrics))]
    (mapv (fn [sub]
            {:label   sub
             :metrics (filterv #(= sub (:sub-section %)) metrics)})
          order)))

(defui section
  "Renders a titled section containing a metric grid.
   Optional :description renders as a subtitle below the title.
   When metrics have :sub-section keys, renders grouped sub-sections."
  [{:keys [label description metrics]}]
  (let [clear-msg (use-memo (fn [] (rand-nth all-clear-messages)) [])]
    (when (seq metrics)
      ($ ui/stack {:gap "md"}
        ($ :div {}
          ($ ui/text {:size "lg" :fw 700} label)
          (when description
            ($ ui/text {:size "sm" :c "dimmed" :mt 2} description)))
        (if (all-clear? metrics)
          ($ ui/alert {:variant "light" :color "teal"
                       :icon ($ icons/icon-circle-check {:size 16})}
            ($ ui/stack {:gap 2}
              ($ ui/text {:size "sm" :fw 600} "All caught up — no action items right now.")
              ($ ui/text {:size "sm" :c "gray.7"} clear-msg)))
          (if (has-sub-sections? metrics)
            (let [groups (group-by-sub-section metrics)]
              ($ ui/stack {:gap "lg"}
                (for [{:keys [label metrics]} groups]
                  ($ :div {:key label}
                    ($ ui/text {:size "sm" :fw 600 :c "dimmed" :tt "uppercase" :mb "xs"}
                      label)
                    ($ metric-grid/metric-grid {:metrics metrics})))))
            ($ metric-grid/metric-grid {:metrics metrics})))))))
